import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import {
  Send,
  Analytics,
  ShoppingCart,
  TrendingUp,
  Insights,
  LocalGroceryStore
} from '@mui/icons-material';
import { 
  getAIProfile,
  getConversationHistory,
  generateProactiveSuggestions,
  learnFromInteraction,
  // eslint-disable-next-line no-unused-vars
  findRecipe,
  // eslint-disable-next-line no-unused-vars
  getAllRecipes,
  // eslint-disable-next-line no-unused-vars
  getCookingGuidance,
  // eslint-disable-next-line no-unused-vars
  getDonenessCheck,
  convertMeasurement
} from '../utils/aiHelper';
import { sendToOpenAI, getMathConversion, generateRecipe } from '../utils/openaiHelper';
import { loadBurtContext, buildBurtContextFromProps } from '../utils/burtContext';
import { isSubscriptionActive } from '../utils/stripeHelper';
import { Star } from '@mui/icons-material';

function BurtChat({ 
  tasks = [], 
  completedTasks = [], 
  familyMembers = [], 
  shoppingList = [],
  familyData = {},
  currentUser,
  addShoppingItem
}) {
  const isPremium = isSubscriptionActive(familyData);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [aiProfile, setAiProfile] = useState(null);
  const [proactiveSuggestions, setProactiveSuggestions] = useState([]);
  const [burtContext, setBurtContext] = useState('');
  const messagesEndRef = useRef(null);

  const checkPremiumAccess = () => {
    if (!isPremium) {
      setShowUpgradeDialog(true);
      return false;
    }
    return true;
  };

  // Initialize with AI profile and conversation history
  useEffect(() => {
    const initializeAI = async () => {
      if (!currentUser?.uid) return;

      try {
        // Load AI profile
        const profile = await getAIProfile(currentUser.uid, 'burt');
        setAiProfile(profile);

        // Load conversation history
        const history = await getConversationHistory(currentUser.uid, 'burt', 20);
        
        if (history.length > 0) {
          // Convert history to message format
          const historyMessages = history.map(msg => ({
            id: msg.id,
            type: msg.messageType === 'user' ? 'user' : 'burt',
            content: msg.content,
            timestamp: new Date(msg.createdAt),
            suggestions: msg.context?.suggestions || []
          }));
          setMessages(historyMessages);
        } else {
          // First time user - show welcome message
          const welcomeMessage = {
            id: 1,
            type: 'burt',
            content: "Hello! I'm Burt, your family's smart assistant. I help with shopping, task management, and family insights. How can I assist you today?",
            timestamp: new Date(),
            suggestions: [
              { text: "Show family analytics", action: "analytics" },
              { text: "Help with shopping", action: "shopping" },
              { text: "Task recommendations", action: "tasks" }
            ]
          };
          setMessages([welcomeMessage]);
        }

        // Generate proactive suggestions
        const currentContext = { tasks, completedTasks, familyMembers, shoppingList, familyData };
        const suggestions = generateProactiveSuggestions(profile, currentContext, 'burt');
        setProactiveSuggestions(suggestions);

        // Set immediate context from props (fast, no Firestore reads)
        const propsContext = buildBurtContextFromProps(tasks, familyMembers, shoppingList, familyData);
        setBurtContext(propsContext);

        // Then load richer context from Firestore (async, non-blocking)
        const familyId = familyData?.id || familyData?.familyId;
        if (familyId) {
          loadBurtContext(familyId, currentUser.uid)
            .then(ctx => setBurtContext(ctx))
            .catch(err => console.warn('Burt Firestore context failed, using props context:', err.message));
        }

      } catch (error) {
        console.error('Error initializing AI:', error);
        // Fallback to basic welcome message
        const welcomeMessage = {
          id: 1,
          type: 'burt',
          content: "Hello! I'm Burt, your family's smart assistant. I help with shopping, task management, and family insights. How can I assist you today?",
          timestamp: new Date(),
          suggestions: [
            { text: "Show family analytics", action: "analytics" },
            { text: "Help with shopping", action: "shopping" },
            { text: "Task recommendations", action: "tasks" }
          ]
        };
        setMessages([welcomeMessage]);
      }
    };

    initializeAI();
  }, [currentUser?.uid, tasks, completedTasks, familyMembers, shoppingList, familyData]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getBurtResponse = async (userMessage) => {
    const message = userMessage.toLowerCase();
    
    // Detect if user is asking to add ingredients to shopping list
    if ((message.includes('add') && (message.includes('ingredient') || message.includes('shopping'))) ||
        (message.includes('shopping list') && (message.includes('add') || message.includes('put')))) {
      // Try to extract ingredients from the message
      const lines = userMessage.split('\n');
      const extractedIngredients = [];
      
      for (const line of lines) {
        const trimmed = line.trim();
        // Look for lines that start with bullets, dashes, or numbers
        if (trimmed.match(/^[•\-*\d]/)) {
          const cleaned = trimmed.replace(/^[•\-*\d]+\.?\s*/, '').trim();
          if (cleaned && cleaned.length > 2) {
            extractedIngredients.push(cleaned);
          }
        }
      }
      
      // If we found ingredients in the message, add them
      if (extractedIngredients.length > 0) {
        try {
          for (const ingredient of extractedIngredients) {
            await addShoppingItem({
              name: ingredient,
              item: ingredient,
              quantity: 1,
              category: 'Groceries',
              price: 0,
              status: 'pending',
              store: 'amazon',
              completed: false,
              addedBy: currentUser.uid,
              requestedBy: 'User via Burt',
              addedAt: new Date().toISOString()
            });
          }
          
          return {
            content: `✅ Perfect! I added ${extractedIngredients.length} ingredient${extractedIngredients.length > 1 ? 's' : ''} to your shopping list:\n\n${extractedIngredients.map(ing => `• ${ing}`).join('\n')}\n\nYou can view them in your shopping list!`,
            suggestions: [
              { text: "View shopping list", action: "shopping" },
              { text: "Find a recipe", action: "find_recipe" }
            ]
          };
        } catch (error) {
          console.error('Error adding ingredients:', error);
        }
      } else {
        // No clear ingredients found, trigger the add ingredients from last recipe
        if (message.includes('from the recipe') || message.includes('from recipe')) {
          await handleAddIngredientsFromAI();
          return null; // handleAddIngredientsFromAI adds its own message
        }
        
        return {
          content: "I'd be happy to add ingredients to your shopping list! Please share them in a list format, like:\n\n• 2 cups flour\n• 1 pound chicken\n• 3 tomatoes\n\nOr you can ask me for a recipe, and I'll add those ingredients automatically!",
          suggestions: [
            { text: "✅ Add ingredients from last recipe", action: "add_recipe_ingredients" },
            { text: "Find a recipe", action: "find_recipe" }
          ]
        };
      }
    }
    
    // Math and conversion questions
    if (message.includes('convert') || message.includes('how many') || message.includes('cup') || message.includes('quart') || message.includes('pound') || message.includes('ounce')) {
      try {
        const mathAnswer = await getMathConversion(userMessage);
        return {
          content: mathAnswer,
          suggestions: [
            { text: "More conversions", action: "conversions" },
            { text: "Cooking measurements", action: "cooking_measurements" },
            { text: "Temperature conversion", action: "temp_conversion" }
          ]
        };
      } catch (error) {
        console.error('Math conversion error:', error);
      }
    }
    
    // General knowledge questions - use OpenAI GPT-4 (Premium)
    if (message.includes('what is') || message.includes('how does') || message.includes('explain') || message.includes('tell me about')) {
      if (!checkPremiumAccess()) {
        return {
          content: "🔒 Burt's advanced AI knowledge is a Premium feature! Upgrade to get instant answers to any question, plus unlimited AI-powered assistance.\n\nStart your 14-day free trial to unlock!",
          suggestions: [
            { text: "🚀 Start Free Trial", action: "upgrade" },
            { text: "View shopping list", action: "shopping" }
          ]
        };
      }
      try {
        const aiResponse = await sendToOpenAI(userMessage, burtContext || 'Parent asking general knowledge question', 'burt');
        return {
          content: aiResponse,
          suggestions: [
            { text: "Ask another question", action: "general_question" },
            { text: "Recipe help", action: "recipe_help" },
            { text: "Shopping help", action: "shopping_help" }
          ]
        };
      } catch (error) {
        console.error('OpenAI error:', error);
      }
    }
    
    // Recipe-related responses
    if (message.includes('recipe') || message.includes('cook') || message.includes('ingredient') ||
        message.includes('make') || message.includes('bake') || message.includes('food') ||
        message.includes('dish') || message.includes('meal')) {

      // If user is asking to manage/store recipes (not requesting one)
      if (message.includes('store') || message.includes('add recipe') || message.includes('save recipe') ||
          message.includes('recipe library') || message.includes('manage recipe')) {
        return {
          content: `I can help you store and manage your family recipes!\n\nYou can add recipes in several ways:\n- Photo Upload: Take a picture of a recipe card or handwritten recipe\n- Manual Entry: Type in your favorite family recipes\n- Ask me for a recipe and save it with the Save button\n\nLook for the "Recipe Collection" card on your dashboard and click "Manage Recipes" to access your library.\n\nOnce recipes are saved, I can add their ingredients straight to your shopping list too!`,
          suggestions: [
            { text: "Show me chicken noodle soup recipe", action: "find_recipe" },
            { text: "Help me make tacos", action: "find_recipe" },
            { text: "What recipes do you know?", action: "list_recipes" }
          ]
        };
      }

      // Extract the food name from the user's message for recipe lookup
      // Strategy: strip verb phrases and filler words, keep the food name
      // "give me a detailed al pastor taco recipe" → "al pastor taco"
      // "how do I make chicken soup" → "chicken soup"
      let recipeQuery = userMessage
        .replace(/\b(give me|show me|find me|get me|look up|search for|can you (find|make|get|show)|please|i want|i need|i'd like)\b/gi, ' ')
        .replace(/\b(find|show|get|make|cook|bake|prepare|how (do i|to) (make|cook|prepare|bake))\b/gi, ' ')
        .replace(/\b(a |an |the |me |some |good |easy |quick |simple |delicious |tasty |best |detailed |authentic |classic |homemade |traditional )\b/gi, ' ')
        .replace(/\b(recipe|recipes|dish|food|meal|dinner|lunch|breakfast|snack)\b/gi, ' ')
        .replace(/\?/g, '')
        .replace(/\s+/g, ' ')
        .trim();

      // Build a human-readable title from the cleaned query
      const titleCase = (str) => str.split(' ').filter(w => w).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
      const recipeTitle = recipeQuery.length > 2 ? titleCase(recipeQuery) : '';

      // Detect if user is specifically requesting a recipe
      const isRequestingRecipe = (
        /\b(find|show|give me|get|make|cook|look up|search|bake|recipe for|how (do i|to) (make|cook))\b/i.test(message) ||
        (message.includes('recipe') && !message.includes('how does') && !message.includes('what is'))
      ) && recipeQuery.length > 2;

      if (isRequestingRecipe) {
        try {
          console.log('Generating structured recipe for:', recipeQuery, '| title:', recipeTitle);
          const recipeText = await generateRecipe(recipeQuery, familyMembers.length || 4);
          return {
            content: recipeText,
            recipeTitle, // store so save handler can use it directly
            suggestions: [
              { text: "Add ingredients to shopping list", action: "add_recipe_ingredients" },
              { text: "Save this recipe", action: "save_recipe" },
              { text: "Find another recipe", action: "find_recipe" }
            ]
          };
        } catch (error) {
          console.error('generateRecipe error:', error);
          // Fall through to generic OpenAI response below
        }
      }

      // General recipe/food question — use OpenAI (Premium)
      if (!checkPremiumAccess()) {
        return {
          content: "🔒 AI recipe suggestions are a Premium feature! Upgrade for unlimited recipe ideas, meal planning, and automatic shopping lists.\n\nStart your 14-day free trial!",
          suggestions: [
            { text: "🚀 Start Free Trial", action: "upgrade" },
            { text: "View shopping list", action: "shopping" }
          ]
        };
      }
      try {
        const context = burtContext
          ? `${burtContext}\n\nUser is asking about recipes or food.`
          : `User is asking about recipes or food. Family has ${familyMembers.length} members.`;
        const aiResponse = await sendToOpenAI(userMessage, context, 'burt');
        return {
          content: aiResponse,
          suggestions: [
            { text: "Add ingredients to shopping list", action: "add_recipe_ingredients" },
            { text: "Save this recipe", action: "save_recipe" },
            { text: "Find another recipe", action: "find_recipe" }
          ]
        };
      } catch (error) {
        console.error('OpenAI error for recipe question:', error);
      }
    }
    
    // Measurement conversion
    if (message.includes('convert') && (message.includes('cup') || message.includes('tbsp') || message.includes('tsp'))) {
      const amountMatch = message.match(/(\d+(?:\/\d+)?)/);
      const fromUnitMatch = message.match(/(cup|cups|tbsp|tbsp|tsp|tsp)/i);
      const toUnitMatch = message.match(/to\s+(cup|cups|tbsp|tbsp|tsp|tsp)/i);
      
      if (amountMatch && fromUnitMatch && toUnitMatch) {
        const amount = parseFloat(amountMatch[1]);
        const fromUnit = fromUnitMatch[1].toLowerCase();
        const toUnit = toUnitMatch[1].toLowerCase();
        
        const conversion = convertMeasurement(amount, fromUnit, toUnit);
        if (conversion) {
          return {
            content: `📏 Measurement Conversion:
${conversion.original} = ${conversion.converted}

💡 Quick reference:
• 1 cup = 16 tbsp = 48 tsp
• 1 tbsp = 3 tsp
• 1 pound = 16 oz`,
            suggestions: [
              { text: "Convert another measurement", action: "convert_measurements" },
              { text: "Show cooking tips", action: "show_cooking_tips" },
              { text: "Help me find a recipe", action: "show_recipes" }
            ]
          };
        }
      }
    }
    
    // Analytics and insights
    if (message.includes('analytics') || message.includes('insights') || message.includes('stats')) {
      const totalTasks = tasks.length;
      const completedCount = completedTasks.length;
      const completionRate = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;
      const totalEarned = completedTasks.reduce((sum, task) => sum + (task.allowance || 0), 0);
      
      return {
        content: `📊 Family Analytics Report:
        
• Task Completion: ${completionRate}% (${completedCount}/${totalTasks})
• Total Allowances Earned: $${totalEarned.toFixed(2)}
• Active Family Members: ${familyMembers.length}
• Shopping Items: ${shoppingList.length}

${completionRate > 80 ? '🌟 Excellent family productivity!' : completionRate > 60 ? '👍 Good progress, room for improvement!' : '💪 Let\'s work on increasing task completion!'}`,
        suggestions: [
          { text: "Show member breakdown", action: "member_stats" },
          { text: "Shopping analysis", action: "shopping_analysis" },
          { text: "Suggest improvements", action: "improvements" }
        ]
      };
    }
    
    // Shopping assistance
    if (message.includes('shopping') || message.includes('shop') || message.includes('list')) {
      const pendingItems = shoppingList.filter(item => !item.approved);
      const approvedItems = shoppingList.filter(item => item.approved);
      const totalValue = approvedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      
      return {
        content: `🛒 Shopping List Analysis:
        
• Pending Approval: ${pendingItems.length} items
• Ready to Buy: ${approvedItems.length} items
• Total Value: $${totalValue.toFixed(2)}
• Categories: ${[...new Set(approvedItems.map(item => item.category))].join(', ')}

${pendingItems.length > 0 ? `💡 You have ${pendingItems.length} items waiting for approval.` : '✅ All items are approved and ready for shopping!'}`,
        suggestions: [
          { text: "Show pending items", action: "pending_items" },
          { text: "Budget analysis", action: "budget_analysis" },
          { text: "Shopping tips", action: "shopping_tips" }
        ]
      };
    }
    
    // Task management
    if (message.includes('task') || message.includes('chore') || message.includes('assign')) {
      const pendingTasks = tasks.filter(task => task.status === 'pending');
      const overdueTasks = tasks.filter(task => 
        task.status === 'pending' && 
        task.dueDate && 
        new Date(task.dueDate) < new Date()
      );
      
      return {
        content: `📋 Task Management Overview:
        
• Pending Tasks: ${pendingTasks.length}
• Overdue Tasks: ${overdueTasks.length}
• Average Allowance: $${tasks.length > 0 ? (tasks.reduce((sum, task) => sum + (task.allowance || 0), 0) / tasks.length).toFixed(2) : '0'}

${overdueTasks.length > 0 ? `⚠️ You have ${overdueTasks.length} overdue tasks that need attention.` : '✅ All tasks are on schedule!'}`,
        suggestions: [
          { text: "Show overdue tasks", action: "overdue_tasks" },
          { text: "Task suggestions", action: "task_suggestions" },
          { text: "Member performance", action: "member_performance" }
        ]
      };
    }
    
    // Member insights
    if (message.includes('member') || message.includes('child') || message.includes('performance')) {
      const memberStats = familyMembers.map(member => {
        const memberTasks = tasks.filter(task => task.assignedTo.includes(member.id));
        const memberCompleted = completedTasks.filter(task => task.assignedTo.includes(member.id));
        const memberEarned = memberCompleted.reduce((sum, task) => sum + (task.allowance || 0), 0);
        
        return {
          name: member.displayName,
          totalTasks: memberTasks.length,
          completed: memberCompleted.length,
          earned: memberEarned,
          streak: member.streak || 0
        };
      });
      
      const topPerformer = memberStats.reduce((best, member) => 
        member.completed > best.completed ? member : best, memberStats[0]);
      
      return {
        content: `👥 Family Member Performance:
        
${memberStats.map(member => 
  `• ${member.name}: ${member.completed}/${member.totalTasks} tasks, $${member.earned.toFixed(2)} earned, ${member.streak}-day streak`
).join('\n')}

🏆 Top Performer: ${topPerformer?.name || 'N/A'} with ${topPerformer?.completed || 0} completed tasks!`,
        suggestions: [
          { text: "Individual breakdown", action: "individual_stats" },
          { text: "Motivation tips", action: "motivation_tips" },
          { text: "Reward suggestions", action: "rewards" }
        ]
      };
    }
    
    // Budget and financial insights
    if (message.includes('budget') || message.includes('money') || message.includes('cost')) {
      const totalEarned = completedTasks.reduce((sum, task) => sum + (task.allowance || 0), 0);
      const shoppingValue = shoppingList.filter(item => item.approved)
        .reduce((sum, item) => sum + (item.price * item.quantity), 0);
      
      return {
        content: `💰 Financial Overview:
        
• Total Allowances Paid: $${totalEarned.toFixed(2)}
• Shopping List Value: $${shoppingValue.toFixed(2)}
• Average Task Value: $${tasks.length > 0 ? (totalEarned / tasks.length).toFixed(2) : '0'}
• Cost per Family Member: $${familyMembers.length > 0 ? (totalEarned / familyMembers.length).toFixed(2) : '0'}

💡 ${shoppingValue > totalEarned ? 'Consider increasing task values or reducing shopping requests.' : 'Great balance between earnings and spending!'}`,
        suggestions: [
          { text: "Budget recommendations", action: "budget_recommendations" },
          { text: "Allowance analysis", action: "allowance_analysis" },
          { text: "Savings suggestions", action: "savings_suggestions" }
        ]
      };
    }
    
    // Help and tips
    if (message.includes('help') || message.includes('tip') || message.includes('advice')) {
      const tips = [
        "Set clear task deadlines to improve completion rates",
        "Use photo requirements to ensure quality work",
        "Balance task difficulty with appropriate allowances",
        "Review shopping requests weekly to manage budget",
        "Celebrate streaks to maintain motivation",
        "Assign collaborative tasks to build family teamwork"
      ];
      const randomTip = tips[Math.floor(Math.random() * tips.length)];
      
      return {
        content: `💡 Pro Tip: ${randomTip}
        
I can help you with:
• Family analytics and insights
• Shopping list optimization
• Task management strategies
• Budget planning
• Member performance tracking`,
        suggestions: [
          { text: "More tips", action: "more_tips" },
          { text: "Family insights", action: "analytics" },
          { text: "Shopping help", action: "shopping" }
        ]
      };
    }
    
    // Default response
    // Fallback to OpenAI GPT-4 for any other questions (Premium)
    if (!checkPremiumAccess()) {
      return {
        content: "🔒 Burt's full AI capabilities are a Premium feature! Upgrade for unlimited AI conversations, smart suggestions, and personalized insights.\n\nStart your 14-day free trial!",
        suggestions: [
          { text: "🚀 Start Free Trial", action: "upgrade" },
          { text: "View analytics", action: "analytics" }
        ]
      };
    }
    try {
      const aiResponse = await sendToOpenAI(userMessage, burtContext || 'General question from parent', 'burt');
      return {
        content: aiResponse,
        suggestions: [
          { text: "Ask another question", action: "general_question" },
          { text: "Recipe help", action: "recipe_help" },
          { text: "Shopping help", action: "shopping_help" }
        ]
      };
    } catch (error) {
      console.error('❌ OpenAI Error:', error);
      console.error('Error details:', error.message);
      
      // Show user-friendly error message
      return {
        content: `⚠️ I'm having trouble connecting to my AI brain right now. Error: ${error.message}\n\nPlease make sure your OpenAI API key is configured in the .env.local file.\n\nIn the meantime, I can still help with basic tasks!`,
        suggestions: [
          { text: "View analytics", action: "analytics" },
          { text: "Shopping help", action: "shopping" },
          { text: "Task management", action: "tasks" }
        ]
      };
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !currentUser?.uid) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentMessage = inputMessage;
    setInputMessage('');
    setIsTyping(true);

    // Simulate typing delay
    setTimeout(async () => {
      try {
        const burtResponse = await getBurtResponse(currentMessage);
        const responseMessage = {
          id: Date.now() + 1,
          type: 'burt',
          content: burtResponse.content,
          timestamp: new Date(),
          suggestions: burtResponse.suggestions,
          recipeTitle: burtResponse.recipeTitle || null // store recipe title for save handler
        };
        
        setMessages(prev => [...prev, responseMessage]);
        
        // Learn from this interaction
        const context = {
          tasks,
          completedTasks,
          familyMembers,
          shoppingList,
          familyData,
          suggestions: burtResponse.suggestions
        };
        
        await learnFromInteraction(
          currentUser.uid, 
          'burt', 
          currentMessage, 
          burtResponse.content, 
          context
        );

        // Update proactive suggestions
        if (aiProfile) {
          const currentContext = { tasks, completedTasks, familyMembers, shoppingList, familyData };
          const newSuggestions = generateProactiveSuggestions(aiProfile, currentContext, 'burt');
          setProactiveSuggestions(newSuggestions);
        }
        
      } catch (error) {
        console.error('Error handling message:', error);
        // Fallback response
        const fallbackMessage = {
          id: Date.now() + 1,
          type: 'burt',
          content: "I'm here to help with family management and insights. What would you like to know?",
          timestamp: new Date(),
          suggestions: [
            { text: "Show family analytics", action: "analytics" },
            { text: "Help with shopping", action: "shopping" }
          ]
        };
        setMessages(prev => [...prev, fallbackMessage]);
      } finally {
        setIsTyping(false);
      }
    }, 1500 + Math.random() * 1000);
  };

  const handleSuggestionClick = (suggestion) => {
    // Handle special recipe actions
    if (suggestion.action === "add_recipe_ingredients") {
      handleAddIngredientsFromAI();
      return;
    }
    
    if (suggestion.action === "save_recipe") {
      handleSaveRecipeFromAI();
      return;
    }
    
    setInputMessage(suggestion.text);
    handleSendMessage();
  };

  const handleAddIngredientsFromAI = async () => {
    if (!addShoppingItem || !currentUser?.uid) {
      console.error('Missing addShoppingItem function or currentUser');
      return;
    }

    // Find the last Burt message with recipe content
    let lastRecipeMessage = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].type === 'burt' && messages[i].content) {
        const content = messages[i].content.toLowerCase();
        if (content.includes('ingredient') || content.includes('recipe') || content.includes('cup') || content.includes('tablespoon')) {
          lastRecipeMessage = messages[i].content;
          break;
        }
      }
    }

    if (!lastRecipeMessage) {
      const errorMessage = {
        id: Date.now(),
        type: 'burt',
        content: "I couldn't find any recipe ingredients in our recent conversation. Please ask me for a recipe first!",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      return;
    }

    try {
      // Parse ingredients from plain-text format (supports both new plain-text and old markdown)
      const ingredients = [];
      const lines = lastRecipeMessage.split('\n');
      let inIngredientSection = false;
      
      for (const line of lines) {
        const trimmed = line.trim();
        
        // Detect ingredient section start — handle: "INGREDIENTS:", "### Ingredients", "**Ingredients:**", "Ingredients:"
        const cleanedHeader = trimmed.replace(/^#+\s*/, '').replace(/\*\*/g, '').trim();
        if (/^ingredients?:?\s*$/i.test(cleanedHeader)) {
          inIngredientSection = true;
          continue;
        }
        
        // Detect section end — instructions, tips, description, etc.
        if (/^(instructions?|directions?|steps?|tips?|notes?|description|prep time|cook time|servings?|difficulty|equipment):?\s*$/i.test(cleanedHeader) &&
            trimmed.length < 50) {
          inIngredientSection = false;
          continue;
        }
        // Also end on numbered steps (instruction lines start with "1.")
        if (/^\d+\.\s/.test(trimmed) && inIngredientSection) {
          inIngredientSection = false;
          // Don't continue — this line is an instruction, not ingredient
        }
        
        if (inIngredientSection) {
          // Clean up markdown and bullet chars
          let cleanLine = trimmed
            .replace(/^[*\-•]\s*/, '')
            .replace(/\*\*/g, '')
            .replace(/^#+\s*/, '')
            .trim();
          
          // Skip empty, headers, or obvious non-ingredients
          if (!cleanLine || cleanLine.length < 3) continue;
          if (cleanLine.endsWith(':')) continue;
          if (/^(for the|for serving|optional|garnish)/i.test(cleanLine)) continue;
          
          if (!ingredients.includes(cleanLine) && cleanLine.length < 120) {
            ingredients.push(cleanLine);
          }
        }
      }

      console.log('📋 Parsed ingredients:', ingredients);

      if (ingredients.length === 0) {
        const errorMessage = {
          id: Date.now(),
          type: 'burt',
          content: "I couldn't find a clear ingredient list in the recipe. Could you ask me to list the ingredients separately? For example, 'List the ingredients for pork tamales'",
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
        return;
      }

      // Add each ingredient to shopping list
      for (const ingredient of ingredients) {
        await addShoppingItem({
          name: ingredient,
          item: ingredient,
          quantity: 1,
          category: 'Recipe Ingredients',
          price: 0,
          status: 'pending',
          store: 'amazon',
          completed: false,
          addedBy: currentUser.uid,
          requestedBy: 'Burt AI',
          addedAt: new Date().toISOString()
        });
      }

      const successMessage = {
        id: Date.now(),
        type: 'burt',
        content: `✅ Success! I added ${ingredients.length} ingredients to your shopping list:

${ingredients.map(ing => `• ${ing}`).join('\n')}

You can view and manage these items in your shopping list!`,
        timestamp: new Date(),
        suggestions: [
          { text: "Show shopping list", action: "shopping" },
          { text: "Find another recipe", action: "find_recipe" }
        ]
      };

      setMessages(prev => [...prev, successMessage]);
    } catch (error) {
      console.error('Error adding ingredients from AI:', error);
      const errorMessage = {
        id: Date.now(),
        type: 'burt',
        content: "Sorry, I had trouble adding the ingredients. Please try again or add them manually to your shopping list.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleSaveRecipeFromAI = async () => {
    if (!currentUser?.uid) {
      console.error('Missing user information');
      return;
    }
    
    // Get familyId from familyData prop or currentUser
    const familyId = familyData?.id || currentUser?.familyId;
    if (!familyId) {
      const errorMessage = {
        id: Date.now(),
        type: 'burt',
        content: "Sorry, I couldn't save the recipe. Please make sure you're part of a family. You can set up your family from the dashboard.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      return;
    }

    // Find the last Burt message that contains a recipe (has INGREDIENTS section)
    let lastRecipeMessage = null;
    let recipeTitle = "AI Recipe";
    let savedRecipeTitle = null;

    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.type === 'burt' && msg.content) {
        const content = msg.content;
        // Must contain an ingredients section — not just any mention of "recipe"
        if (/INGREDIENTS:/i.test(content) || 
            /\*\*ingredients?\*\*/i.test(content) ||
            (/ingredient/i.test(content) && /instruction/i.test(content))) {
          lastRecipeMessage = content;
          // Use the stored recipeTitle from when generateRecipe() was called — most reliable
          savedRecipeTitle = msg.recipeTitle || null;
          break;
        }
      }
    }

    if (lastRecipeMessage) {
      if (savedRecipeTitle) {
        // Best case: we stored the title when the recipe was generated
        recipeTitle = savedRecipeTitle;
      } else {
        // Fallback 1: first non-metadata line of the recipe content
        const lines = lastRecipeMessage.split('\n').map(l => l.trim()).filter(l => l);
        const metaPattern = /^(description|prep time|cook time|servings?|difficulty|INGREDIENTS|INSTRUCTIONS|TIPS|notes?):/i;
        for (const line of lines) {
          const cl = line.replace(/\*\*/g, '').replace(/^#+\s*/, '').trim();
          if (!metaPattern.test(cl) && !cl.endsWith(':') && cl.length > 3 && !/^[-\d]/.test(cl)) {
            recipeTitle = cl;
            break;
          }
        }
        // Fallback 2: user's last message word extraction
        if (recipeTitle === 'AI Recipe') {
          for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].type === 'user') {
              const userMsg = messages[i].content;
              const m = userMsg.match(/(?:make|cook|find|look up|search for|recipe for|bake|show me|give me)\s+(?:a |an |the |me |some )?(?:detailed |authentic |classic )?(.+?)(?:\s+recipe)?(?:\?|$)/i);
              if (m && m[1].trim().length > 2) {
                recipeTitle = m[1].trim().replace(/\?/g, '').split(' ').filter(w => w).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
                break;
              }
            }
          }
        }
      }
    }

    if (!lastRecipeMessage) {
      const errorMessage = {
        id: Date.now(),
        type: 'burt',
        content: "I couldn't find a recipe to save. Please ask me for a recipe first!",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      return;
    }

    try {
      // Parse the recipe content with enhanced structure extraction
      const ingredients = [];
      const instructions = [];
      const tips = [];
      let prepTime = '';
      let cookTime = '';
      let servings = '';
      let difficulty = '';
      let description = '';
      
      const recipeLines = lastRecipeMessage.split('\n');
      let inIngredientsSection = false;
      let inInstructionsSection = false;
      let inTipsSection = false;

      // Helper: clean markdown and heading prefixes from a line
      const cleanLine = (l) => l.trim().replace(/^#+\s*/, '').replace(/\*\*/g, '').trim();

      for (const line of recipeLines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        const cleaned = cleanLine(trimmed);
        
        // Detect section headers (support: "INGREDIENTS:", "### Ingredients", "**Ingredients:**")
        if (/^ingredients?:?\s*$/i.test(cleaned)) {
          inIngredientsSection = true; inInstructionsSection = false; inTipsSection = false; continue;
        }
        if (/^(instructions?|directions?|steps?):?\s*$/i.test(cleaned)) {
          inInstructionsSection = true; inIngredientsSection = false; inTipsSection = false; continue;
        }
        if (/^(tips?|notes?|cooking tips?):?\s*$/i.test(cleaned)) {
          inTipsSection = true; inInstructionsSection = false; inIngredientsSection = false; continue;
        }
        
        // Extract metadata (works for both "Prep Time: 20 min" and "**Prep Time:** 20 min")
        if (/description:/i.test(cleaned)) {
          description = cleaned.replace(/.*description:\s*/i, '').trim();
        } else if (/prep time:/i.test(cleaned)) {
          prepTime = cleaned.replace(/.*prep time:\s*/i, '').trim();
        } else if (/cook time:/i.test(cleaned)) {
          cookTime = cleaned.replace(/.*cook time:\s*/i, '').trim();
        } else if (/servings?:|serves:/i.test(cleaned)) {
          const m = cleaned.match(/(?:servings?|serves?):\s*(.+)/i);
          if (m) servings = m[1].trim();
        } else if (/yields?:|makes:/i.test(cleaned)) {
          const m = cleaned.match(/(?:yields?|makes?):\s*(.+)/i);
          if (m) servings = m[1].trim();
        } else if (/difficulty:/i.test(cleaned)) {
          difficulty = cleaned.replace(/.*difficulty:\s*/i, '').trim();
        }
        
        // Parse ingredient lines
        if (inIngredientsSection && (trimmed.startsWith('-') || trimmed.startsWith('*') || trimmed.startsWith('•'))) {
          const ingText = cleaned.replace(/^[-*•]\s*/, '').trim();
          if (!ingText || ingText.endsWith(':') || /^(for the|for serving|optional|garnish)/i.test(ingText)) continue;
          
          // Parse "2 cups flour" / "1 tablespoon olive oil" / "2 large eggs" / "salt to taste"
          const unitWords = 'cup|cups|tablespoon|tablespoons|teaspoon|teaspoons|tbsp|tsp|pound|pounds|lb|lbs|oz|ounce|ounces|gram|grams|g|kg|ml|liter|liters|quart|quarts|pint|pints|clove|cloves|can|cans|package|packages|pkg|stick|sticks|bunch|bunches|head|heads|sprig|sprigs|pinch|dash|slice|slices|piece|pieces|whole';
          const fullMatch = ingText.match(new RegExp(`^([\\d\\s/½¼¾]+)\\s+(${unitWords})s?\\s+(.+)`, 'i'));
          const sizeMatch = ingText.match(/^([\d\s/½¼¾]+)\s+(large|medium|small|extra-large|jumbo)\s+(.+)/i);
          const qtyMatch = ingText.match(/^([\d\s/½¼¾]+)\s+(.+)/);
          
          if (fullMatch) {
            ingredients.push({ amount: fullMatch[1].trim(), unit: fullMatch[2].trim(), name: fullMatch[3].trim(), category: 'Pantry' });
          } else if (sizeMatch) {
            ingredients.push({ amount: sizeMatch[1].trim(), unit: sizeMatch[2].trim(), name: sizeMatch[3].trim(), category: 'Pantry' });
          } else if (qtyMatch && /^\d/.test(ingText)) {
            ingredients.push({ amount: qtyMatch[1].trim(), unit: '', name: qtyMatch[2].trim(), category: 'Pantry' });
          } else {
            ingredients.push({ amount: '', unit: '', name: ingText, category: 'Pantry' });
          }
        }
        
        // Parse instruction lines
        if (inInstructionsSection) {
          const stepText = cleaned.replace(/^[-*•]\s*/, '').replace(/^\d+\.?\s*/, '').trim();
          if (stepText && !stepText.endsWith(':') && stepText.length > 5) {
            instructions.push(stepText);
          }
        }
        
        // Parse tips
        if (inTipsSection && (trimmed.startsWith('-') || trimmed.startsWith('*') || trimmed.startsWith('•') || /^\d/.test(trimmed))) {
          const tipText = cleaned.replace(/^[-*•]\s*/, '').replace(/^\d+\.?\s*/, '').trim();
          if (tipText) tips.push(tipText);
        }
      }

      // Save to Firestore
      const { collection, addDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      
      const recipeData = {
        title: recipeTitle,
        name: recipeTitle, // Also set name for compatibility
        description: description,
        prepTime: prepTime,
        cookTime: cookTime,
        servings: servings,
        difficulty: difficulty || 'Medium',
        ingredients: ingredients.length > 0 ? ingredients : ['See full recipe content below'],
        instructions: instructions.length > 0 ? instructions : [lastRecipeMessage],
        cookingTips: tips,
        tips: tips, // Store both for compatibility
        source: 'burt-ai',
        sourceModel: 'gpt-4-turbo',
        createdBy: currentUser.uid,
        createdAt: new Date(),
        familyId: familyId,
        fullContent: lastRecipeMessage // Store the full AI response
      };

      await addDoc(collection(db, 'families', familyId, 'recipes'), recipeData);

      const successMessage = {
        id: Date.now(),
        type: 'burt',
        content: `✅ Recipe saved successfully!

"${recipeTitle}" has been added to your family recipe library. You can find it in the Recipe Manager on your dashboard!`,
        timestamp: new Date(),
        suggestions: [
          { text: "Show all recipes", action: "list_recipes" },
          { text: "Find another recipe", action: "find_recipe" }
        ]
      };

      setMessages(prev => [...prev, successMessage]);
    } catch (error) {
      console.error('Error saving recipe:', error);
      const errorMessage = {
        id: Date.now(),
        type: 'burt',
        content: "Sorry, I had trouble saving the recipe. Please try again or save it manually.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Box sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.paper',
      overflow: 'hidden'
    }}>
      {/* Messages Area - Scrollable */}
      <Box sx={{ 
        flexGrow: 1, 
        minHeight: 0, // Critical: allows flex child to shrink
        overflowY: 'auto', 
        overflowX: 'hidden',
        p: { xs: 1, sm: 2 },
        bgcolor: 'rgba(70, 130, 180, 0.05)',
        display: 'flex',
        flexDirection: 'column'
      }}>
          <List sx={{ p: 0 }}>
            {messages.map((message) => (
              <ListItem key={message.id} sx={{ alignItems: 'flex-start', px: 0 }}>
                <ListItemAvatar>
                  <Avatar sx={{ 
                    bgcolor: message.type === 'burt' ? 'rgba(70, 130, 180, 0.8)' : 'primary.main',
                    width: 32,
                    height: 32
                  }}>
                    {message.type === 'burt' ? '🤖' : '👤'}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Paper sx={{ 
                      p: 2, 
                      bgcolor: message.type === 'burt' ? 'rgba(70, 130, 180, 0.1)' : 'primary.light',
                      borderRadius: 2,
                      border: `1px solid ${message.type === 'burt' ? 'rgba(70, 130, 180, 0.3)' : 'primary.main'}`
                    }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                        {message.content}
                      </Typography>
                      
                      {/* Suggestions */}
                      {message.suggestions && (
                        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {message.suggestions.map((suggestion, index) => (
                            <Chip
                              key={index}
                              label={suggestion.text}
                              size="small"
                              onClick={() => handleSuggestionClick(suggestion)}
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'primary.light' }
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </Paper>
                  }
                />
              </ListItem>
            ))}
            
            {isTyping && (
              <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
                <ListItemAvatar>
                  <Avatar sx={{ 
                    bgcolor: 'rgba(70, 130, 180, 0.8)',
                    width: 32,
                    height: 32
                  }}>
                    🤖
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Paper sx={{ 
                      p: 2, 
                      bgcolor: 'rgba(70, 130, 180, 0.1)',
                      borderRadius: 2
                    }}>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Burt is analyzing...
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Box sx={{ 
                            width: 4, 
                            height: 4, 
                            bgcolor: 'primary.main', 
                            borderRadius: '50%',
                            animation: 'typing 1.4s infinite ease-in-out'
                          }} />
                          <Box sx={{ 
                            width: 4, 
                            height: 4, 
                            bgcolor: 'primary.main', 
                            borderRadius: '50%',
                            animation: 'typing 1.4s infinite ease-in-out 0.2s'
                          }} />
                          <Box sx={{ 
                            width: 4, 
                            height: 4, 
                            bgcolor: 'primary.main', 
                            borderRadius: '50%',
                            animation: 'typing 1.4s infinite ease-in-out 0.4s'
                          }} />
                        </Box>
                      </Typography>
                    </Paper>
                  }
                />
              </ListItem>
            )}
          </List>
          <div ref={messagesEndRef} />
          
          {/* Proactive Suggestions */}
          {proactiveSuggestions.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="primary.main" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Insights fontSize="small" />
                Burt's Insights
              </Typography>
              {proactiveSuggestions.slice(0, 2).map((suggestion, index) => (
                <Paper
                  key={index}
                  sx={{
                    p: 2,
                    mb: 1,
                    bgcolor: suggestion.priority === 'high' ? 'warning.light' : 'info.light',
                    border: `1px solid ${suggestion.priority === 'high' ? 'warning.main' : 'info.main'}`,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: suggestion.priority === 'high' ? 'warning.main' : 'info.main',
                      color: 'white'
                    }
                  }}
                  onClick={() => handleSuggestionClick({ text: suggestion.content })}
                >
                  <Typography variant="body2">
                    {suggestion.content}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}
      </Box>

      {/* Input Area - Always Visible at Bottom */}
      <Box sx={{ 
        p: { xs: 1, sm: 2 }, 
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        flexShrink: 0
      }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', mb: 1 }}>
          <TextField
            fullWidth
            multiline
            maxRows={2}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask Burt about family insights, shopping, or tasks..."
            variant="outlined"
            size="small"
            disabled={isTyping}
          />
          <IconButton 
            color="primary" 
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isTyping}
            sx={{ flexShrink: 0, minWidth: 48, minHeight: 48 }}
          >
            <Send />
          </IconButton>
        </Box>
        
        {/* Quick Actions - Show only 2 on mobile, all on desktop */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            startIcon={<Analytics />}
            onClick={() => handleSuggestionClick({ text: "Show family analytics" })}
            variant="outlined"
          >
            Analytics
          </Button>
          <Button
            size="small"
            startIcon={<ShoppingCart />}
            onClick={() => handleSuggestionClick({ text: "Help with shopping" })}
            variant="outlined"
          >
            Shopping
          </Button>
          <Button
            size="small"
            startIcon={<LocalGroceryStore />}
            onClick={() => handleSuggestionClick({ text: "How do I add my own recipes?" })}
            variant="outlined"
            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
          >
            Recipes
          </Button>
          <Button
            size="small"
            startIcon={<TrendingUp />}
            onClick={() => handleSuggestionClick({ text: "Task recommendations" })}
            variant="outlined"
            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
          >
            Tasks
          </Button>
        </Box>
      </Box>

      {/* Upgrade Dialog for Non-Premium Users */}
      <Dialog open={showUpgradeDialog} onClose={() => setShowUpgradeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Star sx={{ color: 'warning.main' }} />
            Unlock Burt's Full AI Power
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Advanced AI features are <strong>Premium</strong>! Upgrade to Earn-E Premium to:
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2">🤖 Unlimited AI conversations</Typography>
            <Typography variant="body2">🧠 Smart, personalized suggestions</Typography>
            <Typography variant="body2">📊 Family insights &amp; analytics</Typography>
            <Typography variant="body2">🍳 AI recipe generation &amp; meal planning</Typography>
            <Typography variant="body2">💬 Natural language shopping assistance</Typography>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              🎉 Start your <strong>14-day free trial</strong> - cancel anytime!
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowUpgradeDialog(false)} variant="outlined">
            Maybe Later
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setShowUpgradeDialog(false);
              window.location.href = '/subscription';
            }}
            startIcon={<Star />}
          >
            Start Free Trial
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BurtChat;
